import "@/App.css";
import { lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";
import Overview from "@/pages/Overview";
import LiveControl from "@/pages/LiveControl";
import Tasks from "@/pages/Tasks";
import HumanDecisions from "@/pages/HumanDecisions";
import Systems from "@/pages/Systems";
import Analytics from "@/pages/Analytics";
import EventLog from "@/pages/EventLog";
import Architecture from "@/pages/Architecture";
import Settings from "@/pages/Settings";
import AcademyHome from "@/pages/Academy/AcademyHome";
import LessonView from "@/pages/Academy/LessonView";
import Glossary from "@/pages/Academy/Glossary";
import HealthLayout from "@/components/HealthLayout";
import HealthLogin from "@/pages/Health/HealthLogin";
import HealthOverview from "@/pages/Health/HealthOverview";
import Patients from "@/pages/Health/Patients";
import PatientDetail from "@/pages/Health/PatientDetail";
import AICore from "@/pages/Health/AICore";
import ProfessionalReview from "@/pages/Health/ProfessionalReview";
import HumanLoop from "@/pages/Health/HumanLoop";
import AuditTrail from "@/pages/Health/AuditTrail";
import PresentationMode from "@/pages/Health/PresentationMode";
import { Validation, Governance, Roadmap } from "@/pages/Health/Phase2";
import { LabProvider } from "@/context/LabContext";
import LabLayout from "@/components/LabLayout";
import { LabAuthProvider, LabGuard, LabLogin, LabSetPassword } from "@/pages/Lab/LabAuth";
const LabDashboard = lazy(() => import("@/pages/Lab/Dashboard"));
const LabProjects = lazy(() => import("@/pages/Lab/Projects"));
const LabSignal = lazy(() => import("@/pages/Lab/SignalLab"));
const LabNetwork = lazy(() => import("@/pages/Lab/NetworkDesigner"));
const LabExperiments = lazy(() => import("@/pages/Lab/Experiments"));
const LabEnergy = lazy(() => import("@/pages/Lab/EnergyLab"));
const LabReports = lazy(() => import("@/pages/Lab/Reports"));
const LabTeam = lazy(() => import("@/pages/Lab/EngineeringTeam"));
const LabHistory = lazy(() => import("@/pages/Lab/History"));
const LabSettings = lazy(() => import("@/pages/Lab/Settings"));

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050B14] text-slate-400 font-mono text-sm">
        Initializing NGX Core…
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/lab/login" element={<LabLogin />} />
            <Route path="/lab/set-password" element={<LabSetPassword />} />
            <Route
              path="/lab"
              element={
                <LabAuthProvider>
                  <LabGuard>
                    <LabProvider>
                      <LabLayout />
                    </LabProvider>
                  </LabGuard>
                </LabAuthProvider>
              }
            >
              <Route index element={<LabDashboard />} />
              <Route path="projects" element={<LabProjects />} />
              <Route path="signal" element={<LabSignal />} />
              <Route path="network" element={<LabNetwork />} />
              <Route path="experiments" element={<LabExperiments />} />
              <Route path="energy" element={<LabEnergy />} />
              <Route path="reports" element={<LabReports />} />
              <Route path="team" element={<LabTeam />} />
              <Route path="history" element={<LabHistory />} />
              <Route path="settings" element={<LabSettings />} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/health/login" element={<HealthLogin />} />
            <Route path="/health" element={<HealthLayout />}>
              <Route index element={<HealthOverview />} />
              <Route path="patients" element={<Patients />} />
              <Route path="patients/:id" element={<PatientDetail />} />
              <Route path="ai-core" element={<AICore />} />
              <Route path="review" element={<ProfessionalReview />} />
              <Route path="human-loop" element={<HumanLoop />} />
              <Route path="audit" element={<AuditTrail />} />
              <Route path="presentation" element={<PresentationMode />} />
              <Route path="validation" element={<Validation />} />
              <Route path="governance" element={<Governance />} />
              <Route path="roadmap" element={<Roadmap />} />
            </Route>
            <Route
              path="/"
              element={
                <Protected>
                  <AppLayout />
                </Protected>
              }
            >
              <Route index element={<Overview />} />
              <Route path="live" element={<LiveControl />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="decisions" element={<HumanDecisions />} />
              <Route path="systems" element={<Systems />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="events" element={<EventLog />} />
              <Route path="architecture" element={<Architecture />} />
              <Route path="settings" element={<Settings />} />
              <Route path="academy" element={<AcademyHome />} />
              <Route path="academy/glossary" element={<Glossary />} />
              <Route path="academy/lesson/:id" element={<LessonView />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
